// Hearts subgame for Classic Card Games.
// 4 players. Pass 3 cards (direction rotates each round), then play 13 tricks.
// Avoid hearts (1pt each) and Q♠ (13pt). Shoot the moon: take all 26pts → opponents +26 each.
// Game ends when any player reaches the target score; lowest total wins.

import { shuffleDeck, cardRank, cardSuit, cardName } from './cards';

const SPADES = 0, HEARTS = 1;
const QUEEN_SPADES = 11;       // rank 11 (Q) + suit 0 (♠) * 13 = 11
const TWO_CLUBS = 1 + 3 * 13; // rank 1  (2) + suit 3 (♣) * 13 = 40

function isHeart(card: number): boolean { return cardSuit(card) === HEARTS; }
function isQueenSpades(card: number): boolean { return card === QUEEN_SPADES; }
function pointValue(card: number): number {
  if (isHeart(card)) return 1;
  if (isQueenSpades(card)) return 13;
  return 0;
}
// Trick-winning value: A=13 (highest), K=12, Q=11, …, 3=2, 2=1
function trickRank(rank: number): number { return rank === 0 ? 13 : rank; }

// ---- Types ----

export interface HeartsConfig { targetScore: number; }
export const DEFAULT_HEARTS_CONFIG: HeartsConfig = { targetScore: 100 };

export interface PlayerInit { id: string; name: string; isBot?: boolean; }

export type HeartsPhase = 'passing' | 'playing' | 'roundOver' | 'complete';
export type HeartsActionType = 'pass' | 'play' | 'nextRound';
export type PassDirection = 'left' | 'right' | 'across' | 'hold';

export interface HeartsPlayer {
  id: string;
  name: string;
  hand: number[];
  passedCards: number[] | null; // null = not yet passed; [] = done (hold round or distributed)
  trickPoints: number;          // points taken this round
  totalScore: number;
  isBot: boolean;
}

export interface TrickCard { playerId: string; card: number; }

export interface HeartsRoundResult {
  shooterId: string | null;
  pointsThisRound: Record<string, number>;
}

export interface HeartsState {
  config: HeartsConfig;
  phase: HeartsPhase;
  roundNumber: number;
  passDirection: PassDirection;
  players: HeartsPlayer[];
  currentTrick: TrickCard[];
  trickLeaderIndex: number; // index of player leading the current trick
  heartsBroken: boolean;
  tricksPlayed: number;
  roundResult: HeartsRoundResult | null;
  rng: number;
  winnerId: string | null;
}

export type HeartsAction =
  | { type: 'pass'; playerId: string; cards: number[] }
  | { type: 'play'; playerId: string; card: number }
  | { type: 'nextRound' };

export interface HeartsView extends HeartsState { viewPlayerId: string | null; }

const PASS_CYCLE: PassDirection[] = ['left', 'right', 'across', 'hold'];

// ---- Core API ----

export function createGame(config: HeartsConfig, players: PlayerInit[], seed = Date.now()): HeartsState {
  // Auto-fill to 4 players with bots if room is smaller
  const all = [...players];
  while (all.length < 4) all.push({ id: `bot-fill-${all.length}`, name: `Bot ${all.length + 1}`, isBot: true });
  const four = all.slice(0, 4);

  const base: HeartsState = {
    config,
    phase: 'passing',
    roundNumber: 0,
    passDirection: 'left',
    players: four.map(p => ({
      id: p.id, name: p.name, hand: [],
      passedCards: null, trickPoints: 0, totalScore: 0, isBot: p.isBot ?? false,
    })),
    currentTrick: [],
    trickLeaderIndex: 0,
    heartsBroken: false,
    tricksPlayed: 0,
    roundResult: null,
    rng: seed | 0,
    winnerId: null,
  };
  return startRound(base);
}

export function applyAction(state: HeartsState, action: HeartsAction): HeartsState {
  switch (action.type) {
    case 'pass':      return applyPass(state, action.playerId, action.cards);
    case 'play':      return applyPlay(state, action.playerId, action.card);
    case 'nextRound': return state.phase === 'roundOver' ? startRound(state) : state;
  }
}

export function getPlayerView(state: HeartsState, playerId: string | null): HeartsView {
  const players = state.players.map(p => ({
    ...p,
    hand: playerId !== null && p.id !== playerId ? [] : p.hand,
    // Show whether others have passed but not which cards
    passedCards: playerId !== null && p.id !== playerId
      ? (p.passedCards !== null ? [] : null)
      : p.passedCards,
  }));
  return { ...state, players, viewPlayerId: playerId };
}

export function describe(config: HeartsConfig): string {
  return [
    'Hearts — 4 Players',
    '',
    'Deal 13 cards each. Before play, pass 3 cards (direction rotates each round: left → right → across → hold).',
    '',
    'Play 13 tricks. Must follow suit if possible.',
    '  • 2♣ leads the very first trick.',
    '  • Hearts cannot be led until "broken" (a heart was discarded on a previous trick).',
    '  • On the first trick, hearts and Q♠ cannot be played unless you have no alternative.',
    '',
    'Points: each ♥ = 1 point, Q♠ = 13 points (26 total per round).',
    'Shoot the Moon: if one player takes all 26 points, every opponent scores +26 instead.',
    '',
    `Game ends when any player reaches ${config.targetScore} points. Lowest total score wins.`,
  ].join('\n');
}

export function getValidActions(state: HeartsState, playerId: string): HeartsActionType[] {
  if (state.phase === 'passing') {
    const p = state.players.find(p => p.id === playerId);
    return p?.passedCards === null ? ['pass'] : [];
  }
  if (state.phase === 'playing') {
    const actorIdx = (state.trickLeaderIndex + state.currentTrick.length) % 4;
    return state.players[actorIdx]?.id === playerId ? ['play'] : [];
  }
  if (state.phase === 'roundOver') return ['nextRound'];
  return [];
}

export function getBotAction(playerId: string, state: HeartsState): HeartsAction {
  if (state.phase === 'passing') {
    const player = state.players.find(p => p.id === playerId)!;
    return { type: 'pass', playerId, cards: choosePassing(player.hand) };
  }
  if (state.phase === 'playing') {
    const player = state.players.find(p => p.id === playerId)!;
    return { type: 'play', playerId, card: choosePlay(state, player) };
  }
  return { type: 'nextRound' };
}

// Exported for UI use: which cards can this player legally play right now
export function validCardsForPlayer(state: HeartsState, playerId: string): number[] {
  if (state.phase !== 'playing') return [];
  const actorIdx = (state.trickLeaderIndex + state.currentTrick.length) % 4;
  if (state.players[actorIdx]?.id !== playerId) return [];
  return validCards(state, state.players[actorIdx]);
}

// ---- Game flow ----

function startRound(state: HeartsState): HeartsState {
  const [deck, rng] = shuffleDeck(state.rng);
  const hands: number[][] = [[], [], [], []];
  for (let i = 0; i < 52; i++) hands[i % 4].push(deck[i]);

  const dir = PASS_CYCLE[state.roundNumber % 4];
  const isHold = dir === 'hold';

  const players = state.players.map((p, i) => ({
    ...p,
    hand: [...hands[i]].sort((a, b) => cardSuit(a) - cardSuit(b) || trickRank(cardRank(a)) - trickRank(cardRank(b))),
    passedCards: isHold ? ([] as number[]) : null,
    trickPoints: 0,
  }));

  const leaderIdx = players.findIndex(p => p.hand.includes(TWO_CLUBS));

  return {
    ...state,
    phase: isHold ? 'playing' : 'passing',
    roundNumber: state.roundNumber + 1,
    passDirection: dir,
    players,
    currentTrick: [],
    trickLeaderIndex: leaderIdx >= 0 ? leaderIdx : 0,
    heartsBroken: false,
    tricksPlayed: 0,
    roundResult: null,
    rng,
  };
}

function applyPass(state: HeartsState, playerId: string, cards: number[]): HeartsState {
  if (state.phase !== 'passing' || cards.length !== 3) return state;
  const pIdx = state.players.findIndex(p => p.id === playerId);
  if (pIdx === -1) return state;
  const player = state.players[pIdx];
  if (player.passedCards !== null) return state;
  if (!cards.every(c => player.hand.includes(c))) return state;

  const players = state.players.map((p, i) =>
    i === pIdx ? { ...p, passedCards: cards } : p
  );

  if (!players.every(p => p.passedCards !== null)) return { ...state, players };
  return distributePassedCards({ ...state, players });
}

function distributePassedCards(state: HeartsState): HeartsState {
  const n = 4;
  const incoming: number[][] = [[], [], [], []];
  for (let i = 0; i < n; i++) {
    const passed = state.players[i].passedCards!;
    const target =
      state.passDirection === 'left'   ? (i + 1) % n :
      state.passDirection === 'right'  ? (i + 3) % n :
      /* across */                       (i + 2) % n;
    incoming[target].push(...passed);
  }

  const players = state.players.map((p, i) => {
    const newHand = [
      ...p.hand.filter(c => !p.passedCards!.includes(c)),
      ...incoming[i],
    ].sort((a, b) => cardSuit(a) - cardSuit(b) || trickRank(cardRank(a)) - trickRank(cardRank(b)));
    return { ...p, hand: newHand, passedCards: [] };
  });

  const leaderIdx = players.findIndex(p => p.hand.includes(TWO_CLUBS));
  return { ...state, phase: 'playing', players, trickLeaderIndex: leaderIdx >= 0 ? leaderIdx : 0 };
}

function applyPlay(state: HeartsState, playerId: string, card: number): HeartsState {
  if (state.phase !== 'playing') return state;
  const actorIdx = (state.trickLeaderIndex + state.currentTrick.length) % 4;
  const actor = state.players[actorIdx];
  if (actor.id !== playerId || !actor.hand.includes(card)) return state;
  if (!validCards(state, actor).includes(card)) return state;

  const newHeartsBroken = state.heartsBroken || isHeart(card);
  const trick = [...state.currentTrick, { playerId, card }];
  const players = state.players.map((p, i) =>
    i === actorIdx ? { ...p, hand: p.hand.filter(c => c !== card) } : p
  );

  if (trick.length < 4) {
    return { ...state, currentTrick: trick, players, heartsBroken: newHeartsBroken };
  }

  return resolveTrick({ ...state, currentTrick: trick, players, heartsBroken: newHeartsBroken });
}

function resolveTrick(state: HeartsState): HeartsState {
  const { currentTrick } = state;
  const ledSuit = cardSuit(currentTrick[0].card);

  let winIdx = 0;
  let winTV = trickRank(cardRank(currentTrick[0].card));
  for (let i = 1; i < 4; i++) {
    const tc = currentTrick[i];
    if (cardSuit(tc.card) === ledSuit && trickRank(cardRank(tc.card)) > winTV) {
      winTV = trickRank(cardRank(tc.card));
      winIdx = i;
    }
  }

  const pts = currentTrick.reduce((s, tc) => s + pointValue(tc.card), 0);
  const winnerPlayerId = currentTrick[winIdx].playerId;
  const winnerIdx = state.players.findIndex(p => p.id === winnerPlayerId);

  const players = state.players.map((p, i) =>
    i === winnerIdx ? { ...p, trickPoints: p.trickPoints + pts } : p
  );
  const tricksPlayed = state.tricksPlayed + 1;

  if (tricksPlayed < 13) {
    return { ...state, players, currentTrick: [], trickLeaderIndex: winnerIdx, tricksPlayed };
  }
  return endRound({ ...state, players, currentTrick: [], trickLeaderIndex: winnerIdx, tricksPlayed });
}

function endRound(state: HeartsState): HeartsState {
  const shooter = state.players.find(p => p.trickPoints === 26) ?? null;

  const pointsThisRound: Record<string, number> = {};
  const players = state.players.map(p => {
    const pts = shooter ? (shooter.id === p.id ? 0 : 26) : p.trickPoints;
    pointsThisRound[p.id] = pts;
    return { ...p, totalScore: p.totalScore + pts };
  });

  const roundResult: HeartsRoundResult = { shooterId: shooter?.id ?? null, pointsThisRound };
  const gameOver = players.some(p => p.totalScore >= state.config.targetScore);

  if (!gameOver) return { ...state, phase: 'roundOver', players, roundResult, winnerId: null };

  const winner = players.reduce((best, p) => p.totalScore < best.totalScore ? p : best);
  return { ...state, phase: 'complete', players, roundResult, winnerId: winner.id };
}

// ---- Valid card computation ----

function validCards(state: HeartsState, player: HeartsPlayer): number[] {
  const { hand } = player;
  const isFirstTrick = state.tricksPlayed === 0;
  const isLeading = state.currentTrick.length === 0;

  if (isFirstTrick && isLeading) return [TWO_CLUBS];

  if (isLeading) {
    if (!state.heartsBroken) {
      const nonHearts = hand.filter(c => !isHeart(c));
      if (nonHearts.length > 0) return nonHearts;
    }
    return hand;
  }

  const ledSuit = cardSuit(state.currentTrick[0].card);
  const following = hand.filter(c => cardSuit(c) === ledSuit);
  if (following.length > 0) return following;

  // Void in led suit — on first trick, can't play hearts or Q♠ if alternatives exist
  if (isFirstTrick) {
    const safe = hand.filter(c => !isHeart(c) && !isQueenSpades(c));
    if (safe.length > 0) return safe;
  }
  return hand;
}

// ---- Bot: passing ----

function choosePassing(hand: number[]): number[] {
  const scored = hand.map(card => ({ card, score: passDanger(card, hand) }));
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 3).map(s => s.card);
}

function passDanger(card: number, hand: number[]): number {
  if (isQueenSpades(card)) return 500;
  const rank = cardRank(card);
  const suit = cardSuit(card);
  const tv = trickRank(rank);

  // High hearts are most dangerous to hold
  if (suit === HEARTS) return tv * 10; // A♥=130, K♥=120, Q♥=110, ...

  // High spades can catch Q♠ from an opponent passing it to us
  if (suit === SPADES && !hand.includes(QUEEN_SPADES) && tv >= 12) return tv * 8;

  // Bonus for creating a void in a short suit
  const suitCount = hand.filter(c => cardSuit(c) === suit).length;
  if (suitCount <= 3) return tv + (4 - suitCount) * 12;

  return Math.floor(tv * 0.5);
}

// ---- Bot: playing ----

function choosePlay(state: HeartsState, player: HeartsPlayer): number {
  const valid = validCards(state, player);
  if (valid.length === 1) return valid[0];

  const moonThreat = detectMoonThreat(state, player.id);
  const isLeading = state.currentTrick.length === 0;

  return isLeading
    ? chooseLead(state, player, valid, moonThreat)
    : chooseFollow(state, player, valid, moonThreat);
}

function chooseLead(state: HeartsState, player: HeartsPlayer, valid: number[], moonThreat: string | null): number {
  // Moon-shooting: lead highest hearts to collect all points
  if (shouldShoot(state, player)) {
    const hearts = valid.filter(isHeart);
    return hearts.length > 0 ? highest(hearts) : highest(valid);
  }

  // Disrupting an opponent's moon attempt: get into the lead
  if (moonThreat && moonThreat !== player.id) {
    const nonHearts = valid.filter(c => !isHeart(c));
    return lowest(nonHearts.length > 0 ? nonHearts : valid);
  }

  // Normal: lead from shortest safe suit to create voids
  const safe = valid.filter(c => !isHeart(c) && !isQueenSpades(c));
  if (safe.length === 0) return lowest(valid);

  const bySuit = groupBySuit(safe);
  const shortest = Object.values(bySuit).reduce((a, b) => a.length <= b.length ? a : b);
  return lowest(shortest);
}

function chooseFollow(state: HeartsState, player: HeartsPlayer, valid: number[], moonThreat: string | null): number {
  const ledSuit = cardSuit(state.currentTrick[0].card);
  const isVoid = valid.some(c => cardSuit(c) !== ledSuit);

  if (isVoid) return chooseDump(valid, moonThreat, player.id);

  // Following suit
  const winnerCard = trickWinner(state.currentTrick);
  const winTV = trickRank(cardRank(winnerCard.card));
  const trickHasPoints = state.currentTrick.some(tc => pointValue(tc.card) > 0);

  // Disrupt a moon shooter: win the trick even if it has points
  if (moonThreat && moonThreat !== player.id) {
    const canWin = valid.filter(c => trickRank(cardRank(c)) > winTV);
    if (canWin.length > 0) return lowest(canWin);
  }

  if (trickHasPoints) {
    // Try to lose: play highest card that still loses
    const canLose = valid.filter(c => trickRank(cardRank(c)) < winTV);
    if (canLose.length > 0) return highest(canLose);
    // Forced to win: play lowest
    return lowest(valid);
  }

  // No points yet: winning is okay, play aggressively (but not Q♠ if we can avoid it)
  const safeWin = valid.filter(c => !isQueenSpades(c));
  return highest(safeWin.length > 0 ? safeWin : valid);
}

function chooseDump(valid: number[], moonThreat: string | null, myId: string): number {
  // When disrupting a moon shooter, preserve high cards for winning later
  if (moonThreat && moonThreat !== myId) {
    const nonPoints = valid.filter(c => pointValue(c) === 0);
    if (nonPoints.length > 0) return lowest(nonPoints);
  }

  // Dump the most dangerous card first
  if (valid.some(isQueenSpades)) return QUEEN_SPADES;
  const hearts = valid.filter(isHeart);
  if (hearts.length > 0) return highest(hearts);
  return highest(valid); // dump highest non-point card to reduce future trick-winning risk
}

function shouldShoot(state: HeartsState, player: HeartsPlayer): boolean {
  // Only attempt if opponents have no points yet, early in the round, and we hold many high cards
  if (state.tricksPlayed > 5) return false;
  const anyOpponentHasPoints = state.players.some(p => p.id !== player.id && p.trickPoints > 0);
  if (anyOpponentHasPoints) return false;
  const hearts = player.hand.filter(isHeart);
  const hasQS = player.hand.includes(QUEEN_SPADES) || player.trickPoints >= 13;
  const highHearts = hearts.filter(c => trickRank(cardRank(c)) >= 9);
  return highHearts.length >= 6 && hasQS;
}

function detectMoonThreat(state: HeartsState, myId: string): string | null {
  for (const p of state.players) {
    if (p.id === myId) continue;
    if (p.trickPoints >= 15) {
      const othersHavePoints = state.players.some(q => q.id !== p.id && q.trickPoints > 0);
      if (!othersHavePoints) return p.id;
    }
  }
  return null;
}

// ---- Utilities ----

function highest(cards: number[]): number {
  return cards.reduce((best, c) => trickRank(cardRank(c)) > trickRank(cardRank(best)) ? c : best);
}

function lowest(cards: number[]): number {
  return cards.reduce((best, c) => trickRank(cardRank(c)) < trickRank(cardRank(best)) ? c : best);
}

function groupBySuit(cards: number[]): Record<number, number[]> {
  const g: Record<number, number[]> = {};
  for (const c of cards) { const s = cardSuit(c); if (!g[s]) g[s] = []; g[s].push(c); }
  return g;
}

function trickWinner(trick: TrickCard[]): TrickCard {
  const ledSuit = cardSuit(trick[0].card);
  return trick.reduce((best, tc) =>
    cardSuit(tc.card) === ledSuit && trickRank(cardRank(tc.card)) > trickRank(cardRank(best.card))
      ? tc : best
  );
}

// Exported helpers for CLI / UI display
export { cardName, QUEEN_SPADES, TWO_CLUBS, isHeart, isQueenSpades, pointValue, trickRank };
